import React, { useState, useCallback, memo, useEffect } from "react";
import {
  MenuItem,
  Menu,
  Checkbox,
  FormControlLabel,
  Typography,
  Tooltip,
  IconButton,
  Divider,
  makeStyles,
  Button,
} from "@material-ui/core";
import { ReactComponent as ColumnSelectionIcon } from "../assets/ColumnSelectionIcon.svg";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles((theme) => ({
  columnMenuButton: {
    marginLeft: theme.spacing(1),
  },
  columnMenuItem: {
    padding: theme.spacing(0.5, 2),
    width: "100%",
  },
  columnMenuHeader: {
    padding: theme.spacing(1, 2),
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  // Unified icon button styling to match TableControls
  iconButton: {
    width: 32,
    height: 32,
    padding: 0,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    transition: "box-shadow 200ms ease, transform 150ms ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
      transform: "translateY(-1px)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    "&.Mui-disabled": {
      color: theme.palette.action.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  icon: {
    width: 32,
    height: 32,
    display: "block",
  },
  actionButtonsContainer: {
    padding: theme.spacing(1, 2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  actionButtonsGroup: {
    display: "flex",
    gap: theme.spacing(1),
  },
  actionButton: {
    minWidth: 65,
    fontSize: "0.775rem",
  },
  closeButton: {
    minWidth: 32,
    fontSize: "0.775rem",
    height: 30,
    width: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  menuContainer: {
    minWidth: 280,
    maxHeight: 400,
  },
  menuItemsContainer: {
    maxHeight: 300,
    overflowY: "auto",
  },
  formControl: {
    width: "100%",
    margin: 0,
  },
}));

const ColumnSelectionMenu = memo(
  ({
    columnDefs = [],
    selectedColumns = [],
    defaultVisibleColumns = [],
    onColumnSelectionChange,
  }) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [tempSelectedColumns, setTempSelectedColumns] =
      useState(selectedColumns);

    useEffect(() => {
      setTempSelectedColumns(selectedColumns);
    }, [selectedColumns]);

    const handleOpenMenu = useCallback(
      (event) => {
        setAnchorEl(event.currentTarget);
        setTempSelectedColumns(selectedColumns);
      },
      [selectedColumns]
    );

    const handleCloseMenu = useCallback(() => {
      setAnchorEl(null);
    }, []);

    // Toggle handler for both checkbox and label
    const handleCheckboxToggle = useCallback((columnField) => {
      setTempSelectedColumns((prev) =>
        prev.includes(columnField)
          ? prev.filter((field) => field !== columnField)
          : [...prev, columnField]
      );
    }, []);

    const handleApplyChanges = useCallback(() => {
      if (onColumnSelectionChange) {
        onColumnSelectionChange(tempSelectedColumns);
      }
      handleCloseMenu();
    }, [tempSelectedColumns, onColumnSelectionChange, handleCloseMenu]);

    const handleCancelChanges = useCallback(() => {
      setTempSelectedColumns(selectedColumns);
      handleCloseMenu();
    }, [selectedColumns, handleCloseMenu]);

    const handleResetToDefault = useCallback(() => {
      setTempSelectedColumns(defaultVisibleColumns);
      if (onColumnSelectionChange) {
        onColumnSelectionChange(defaultVisibleColumns);
      }
      handleCloseMenu();
    }, [defaultVisibleColumns, onColumnSelectionChange, handleCloseMenu]);

    return (
      <>
        <Tooltip title="Show/Hide Columns">
          <IconButton
            className={classes.iconButton}
            color="primary"
            onClick={handleOpenMenu}
            aria-label="Column Selection"
          >
            <ColumnSelectionIcon className={classes.icon} />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleCancelChanges}
          PaperProps={{
            className: classes.menuContainer,
          }}
        >
          <Typography variant="subtitle2" className={classes.columnMenuHeader}>
            Select Columns
          </Typography>
          <Divider />

          <div className={classes.menuItemsContainer}>
            {columnDefs.map((col) => {
              const checkboxId = `column-checkbox-${col.field}`;
              const isChecked = tempSelectedColumns.includes(col.field);

              return (
                <MenuItem
                  key={col.field}
                  className={classes.columnMenuItem}
                  disableGutters
                  style={{ paddingLeft: 0, paddingRight: 0 }}
                >
                  <FormControlLabel
                    className={classes.formControl}
                    control={
                      <Checkbox
                        id={checkboxId}
                        checked={isChecked}
                        onChange={() => handleCheckboxToggle(col.field)}
                        color="primary"
                      />
                    }
                    label={col.headerName}
                    htmlFor={checkboxId}
                  />
                </MenuItem>
              );
            })}
          </div>

          <Divider />

          <div className={classes.actionButtonsContainer}>
            <div className={classes.actionButtonsGroup}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyChanges}
                size="small"
                className={classes.actionButton}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleResetToDefault}
                size="small"
                className={classes.actionButton}
              >
                Reset
              </Button>
            </div>
            <IconButton
              onClick={handleCancelChanges}
              size="small"
              className={classes.closeButton}
              aria-label="Close"
              style={{ padding: "4px" }}
            >
              <CloseIcon style={{ fontSize: "1.2rem" }} />
            </IconButton>
          </div>
        </Menu>
      </>
    );
  }
);

export default ColumnSelectionMenu;
